/**
 * Response style picker — personas + Grounded in one popover by the composer.
 * Replaces the old always-visible chip bar: quiet by default, rich on open.
 * The footer caption always states where changes land (this chat vs default),
 * so per-chat overrides never silently rewrite workspace defaults.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Crosshair } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  PERSONAS,
  PERSONA_ICONS,
  getPersona,
  type PersonaId,
} from '@/lib/workspace/personas';
import { Toggle } from '@/components/ui/toggle';

interface StylePickerProps {
  personaId: PersonaId | string;
  onPersonaChange: (id: PersonaId) => void;
  groundedOnly: boolean;
  onGroundedChange: (on: boolean) => void;
  /** Where edits land — drives the footer caption. */
  scope: 'chat' | 'default';
  disabled?: boolean;
  /** Popover opens upward (composer) or downward */
  placement?: 'up' | 'down';
  className?: string;
}

export function StylePicker({
  personaId,
  onPersonaChange,
  groundedOnly,
  onGroundedChange,
  scope,
  disabled,
  placement = 'up',
  className,
}: StylePickerProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const persona = getPersona(personaId);
  const PersonaIcon = PERSONA_ICONS[persona.id];
  const customized = persona.id !== 'default' || groundedOnly;

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 8;
    const menuWidth = 300;
    const pad = 12;
    let left = rect.left;
    left = Math.max(pad, Math.min(left, window.innerWidth - menuWidth - pad));
    if (placement === 'up') {
      const spaceAbove = rect.top - pad;
      setMenuPos({
        bottom: window.innerHeight - rect.top + gap,
        left,
        maxHeight: Math.min(420, Math.max(220, spaceAbove - gap)),
      });
    } else {
      const spaceBelow = window.innerHeight - rect.bottom - pad;
      setMenuPos({
        top: rect.bottom + gap,
        left,
        maxHeight: Math.min(420, Math.max(220, spaceBelow - gap)),
      });
    }
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={cn('relative flex items-center min-w-0', className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Response style & grounding"
        onClick={() => {
          if (!open) updatePosition();
          setOpen((o) => !o);
        }}
        className={cn(
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] transition-colors disabled:opacity-50 max-w-[180px] min-w-0',
          open
            ? 'text-[#e8e6e3] bg-[#1a1a1a]'
            : customized
              ? 'text-[#c8c6c3] hover:text-[#e8e6e3] hover:bg-[#1a1a1a]'
              : 'text-[#5a5a5a] hover:text-[#c8c6c3] hover:bg-[#1a1a1a]',
        )}
      >
        <PersonaIcon className="w-3.5 h-3.5 shrink-0 opacity-80" />
        <span className="truncate font-medium">
          {persona.id === 'default' ? 'Style' : persona.short}
        </span>
        {groundedOnly && (
          <Crosshair
            className="w-3 h-3 shrink-0 text-amber-400/90"
            aria-label="Grounded on"
          />
        )}
        <ChevronDown
          className={cn(
            'w-3 h-3 shrink-0 opacity-60 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && menuPos && (
        <div
          ref={menuRef}
          role="dialog"
          aria-label="Response style"
          className="fixed w-[300px] bg-[#161616] border border-[#2a2a2a] rounded-xl shadow-2xl z-[100] flex flex-col overflow-hidden od-enter"
          style={{
            left: menuPos.left,
            top: menuPos.top,
            bottom: menuPos.bottom,
            maxHeight: menuPos.maxHeight,
          }}
        >
          <div className="overflow-y-auto scrollbar-thin py-1.5">
            <p className="px-3.5 pt-1.5 pb-1 text-[10px] uppercase tracking-wider text-[#3a3a3a] font-semibold">
              Response style
            </p>
            {PERSONAS.map((p) => {
              const active = persona.id === p.id;
              const Icon = PERSONA_ICONS[p.id];
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onPersonaChange(p.id)}
                  className={cn(
                    'w-full text-left px-3 py-2 transition-colors flex items-start gap-2.5',
                    active ? 'bg-[#1f1f1f]' : 'hover:bg-[#222]',
                  )}
                >
                  <Icon
                    className={cn(
                      'w-4 h-4 shrink-0 mt-0.5',
                      active ? 'text-[#e8e6e3]' : 'text-[#5a5a5a]',
                    )}
                  />
                  <span className="flex-1 min-w-0">
                    <span
                      className={cn(
                        'block text-[13px] leading-snug',
                        active ? 'text-[#e8e6e3] font-medium' : 'text-[#c8c6c3]',
                      )}
                    >
                      {p.label}
                    </span>
                    <span className="block text-[11px] text-[#5a5a5a] mt-0.5 leading-snug">
                      {p.description}
                    </span>
                  </span>
                  {active && (
                    <Check className="w-3.5 h-3.5 text-[#e8e6e3] shrink-0 mt-1" />
                  )}
                </button>
              );
            })}

            <div className="mx-3 my-1.5 border-t border-[#242424]" />

            {/* Grounded — toggle row, keeps popover open */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => onGroundedChange(!groundedOnly)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onGroundedChange(!groundedOnly);
                }
              }}
              className="w-full text-left px-3 py-2 transition-colors hover:bg-[#222] flex items-start gap-2.5 cursor-pointer"
            >
              <Crosshair
                className={cn(
                  'w-4 h-4 shrink-0 mt-0.5 transition-colors',
                  groundedOnly ? 'text-amber-400/90' : 'text-[#5a5a5a]',
                )}
              />
              <span className="flex-1 min-w-0">
                <span
                  className={cn(
                    'block text-[13px] leading-snug',
                    groundedOnly ? 'text-amber-200/90 font-medium' : 'text-[#c8c6c3]',
                  )}
                >
                  Grounded
                </span>
                <span className="block text-[11px] text-[#5a5a5a] mt-0.5 leading-snug">
                  Prefer project memory, context & attachments — less invention
                </span>
              </span>
              <span className="mt-0.5 shrink-0">
                <Toggle
                  checked={groundedOnly}
                  onChange={onGroundedChange}
                  label="Grounded"
                  accent="amber"
                />
              </span>
            </div>
          </div>

          <div className="border-t border-[#242424] px-3.5 py-2 shrink-0">
            <p className="text-[10px] text-[#4a4a4a] leading-snug">
              {scope === 'chat'
                ? 'Applies to this chat only'
                : 'Default for new chats — override per chat anytime'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
